import React, { useState } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { showMainToast } from "utils/showToast";

/*
 * Certificate / ID card download buttons.
 *
 * SAVE AS:  ./components/RetailerCredentialButtons.jsx
 *           (next to PermissonManagerComponent, which this page already
 *            imports from ./components/)
 *
 * IMPORT:   import RetailerCredentialButtons from "./components/RetailerCredentialButtons";
 *
 * USE:      <RetailerCredentialButtons userId={effectiveUserId} />
 *
 * Works for both audiences with no branching at the call site. An admin opens
 * the page with ?userid=123 and effectiveUserId is that retailer; a retailer
 * opens their own settings and effectiveUserId is themselves. The server does
 * the rest: your own is always allowed, someone else's only if they are in
 * your downline.
 *
 * WHY IT MINTS A LINK INSTEAD OF LINKING STRAIGHT AT THE PDF
 * ----------------------------------------------------------
 * /retailer/certificate takes a signed token, not ?user_id=. If it took a user
 * id, editing the number in the URL would print somebody else's certificate --
 * and the endpoint has to stay open, because no Authorization header survives
 * a window.open or a phone's PDF viewer. So the panel asks for a link and the
 * link carries the proof.
 *
 * ELIGIBILITY IS NOT DECIDED HERE
 * -------------------------------
 * A retailer whose KYC is not approved gets a 409 with a sentence saying so,
 * and that sentence is shown as-is. Re-implementing the rule in the browser
 * would only create a second place for it to be wrong. The `show` prop below
 * is about tidiness, not permission.
 */

const API_BASE = "https://pro.mokshiq.in";

export default function RetailerCredentialButtons({ userId, className = "" }) {
    const [busy, setBusy] = useState("");

    const open = async (kind) => {
        setBusy(kind);
        try {
            const body = new URLSearchParams();
            if (userId) body.append("user_id", String(userId));

            const res = await fetchWrapper(`${API_BASE}/retailer/credential/link`, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: body.toString(),
            });

            if (!res?.status) {
                showMainToast(res?.message || "Could not prepare the document", "danger");
                return;
            }
            window.open(
                kind === "cert" ? res.data.certificate_url : res.data.id_card_url,
                "_blank",
                "noopener,noreferrer"
            );
        } catch (err) {
            showMainToast("Could not reach the server", "danger");
        } finally {
            setBusy("");
        }
    };

    return (
        <div className={`d-flex gap-2 ${className}`}>
            <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={busy !== ""}
                onClick={() => open("cert")}
            >
                <i className="ph-duotone ph-certificate me-1"></i>
                {busy === "cert" ? "Preparing..." : "Certificate"}
            </button>

            <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={busy !== ""}
                onClick={() => open("card")}
            >
                <i className="ph-duotone ph-identification-card me-1"></i>
                {busy === "card" ? "Preparing..." : "ID Card"}
            </button>
        </div>
    );
}